import {
  Capacitor,
  registerPlugin,
  type PluginListenerHandle,
} from '@capacitor/core';
import {isMobileApp} from '@common/core/platform-detection';
import {
  createEmptySiriAuthorizationRequestResponse,
  createEmptySiriAuthorizationStatusResponse,
  createEmptyPlaybackState,
  createEmptyQueueSnapshot,
  type CarConnectionChangedEvent,
  type NativePlaybackConfig,
  type NativePlaybackLanguageOptions,
  type NativePlaybackPlugin,
  type SiriAuthorizationRequestResponse,
  type SiriAuthorizationStatusResponse,
  type NativePlaybackStateResponse,
  type LimitReachedEvent,
  type PlaybackStateChangedEvent,
  type QueueChangedEvent,
  type RemoteCommandReceivedEvent,
} from '@common/core/capacitor/native-playback-contract';

const PLUGIN_NAME = 'NativePlayback';
const NativePlayback = registerPlugin<NativePlaybackPlugin>(PLUGIN_NAME);

class NativePlaybackBridge {
  isSupported(): boolean {
    return (
      isMobileApp() &&
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable(PLUGIN_NAME)
    );
  }

  async configure(config: NativePlaybackConfig): Promise<void> {
    await this.safeCall(() => NativePlayback.configure(config));
  }

  async setLanguage(options: NativePlaybackLanguageOptions): Promise<void> {
    await this.safeCall(() => NativePlayback.setLanguage(options));
  }

  async removeAllListeners(): Promise<void> {
    await this.safeCall(() => NativePlayback.removeAllListeners());
  }

  async clearSession(): Promise<void> {
    await this.safeCall(() => NativePlayback.clearSession());
  }

  async cue(options: Parameters<NativePlaybackPlugin['cue']>[0]) {
    await this.safeCall(() => NativePlayback.cue(options));
  }

  async replaceQueue(
    options: Parameters<NativePlaybackPlugin['replaceQueue']>[0],
  ) {
    await this.safeCall(() => NativePlayback.replaceQueue(options));
  }

  async appendToQueue(
    options: Parameters<NativePlaybackPlugin['appendToQueue']>[0],
  ) {
    await this.safeCall(() => NativePlayback.appendToQueue(options));
  }

  async addToQueue(options: Parameters<NativePlaybackPlugin['addToQueue']>[0]) {
    await this.safeCall(() => NativePlayback.addToQueue(options));
  }

  async removeFromQueue(
    options: Parameters<NativePlaybackPlugin['removeFromQueue']>[0],
  ) {
    await this.safeCall(() => NativePlayback.removeFromQueue(options));
  }

  async setQueue(snapshot: Parameters<NativePlaybackPlugin['setQueue']>[0]) {
    await this.safeCall(() => NativePlayback.setQueue(snapshot));
  }

  async play(): Promise<void> {
    await this.safeCall(() => NativePlayback.play());
  }

  async pause(): Promise<void> {
    await this.safeCall(() => NativePlayback.pause());
  }

  async stop(): Promise<void> {
    await this.safeCall(() => NativePlayback.stop());
  }

  async seekTo(options: Parameters<NativePlaybackPlugin['seekTo']>[0]) {
    await this.safeCall(() => NativePlayback.seekTo(options));
  }

  async skipToNext(): Promise<void> {
    await this.safeCall(() => NativePlayback.skipToNext());
  }

  async skipToPrevious(): Promise<void> {
    await this.safeCall(() => NativePlayback.skipToPrevious());
  }

  async skipToIndex(options: Parameters<NativePlaybackPlugin['skipToIndex']>[0]) {
    await this.safeCall(() => NativePlayback.skipToIndex(options));
  }

  async setVolume(options: Parameters<NativePlaybackPlugin['setVolume']>[0]) {
    await this.safeCall(() => NativePlayback.setVolume(options));
  }

  async setMuted(options: Parameters<NativePlaybackPlugin['setMuted']>[0]) {
    await this.safeCall(() => NativePlayback.setMuted(options));
  }

  async setPlaybackRate(
    options: Parameters<NativePlaybackPlugin['setPlaybackRate']>[0],
  ) {
    await this.safeCall(() => NativePlayback.setPlaybackRate(options));
  }

  async setRepeatMode(
    options: Parameters<NativePlaybackPlugin['setRepeatMode']>[0],
  ) {
    await this.safeCall(() => NativePlayback.setRepeatMode(options));
  }

  async setShuffle(options: Parameters<NativePlaybackPlugin['setShuffle']>[0]) {
    await this.safeCall(() => NativePlayback.setShuffle(options));
  }

  async getState(): Promise<NativePlaybackStateResponse> {
    return this.safeCall(
      () => NativePlayback.getState(),
      {
        state: createEmptyPlaybackState(),
        queue: createEmptyQueueSnapshot(),
      },
    );
  }

  async getSiriAuthorizationStatus(): Promise<SiriAuthorizationStatusResponse> {
    return this.safeCall(
      () => NativePlayback.getSiriAuthorizationStatus(),
      createEmptySiriAuthorizationStatusResponse(),
    );
  }

  async requestSiriAuthorization(): Promise<SiriAuthorizationRequestResponse> {
    return this.safeCall(
      () => NativePlayback.requestSiriAuthorization(),
      createEmptySiriAuthorizationRequestResponse(),
    );
  }

  async addPlaybackStateChangedListener(
    listener: (event: PlaybackStateChangedEvent) => void,
  ): Promise<(() => void) | null> {
    return this.addListener('playbackStateChanged', listener);
  }

  async addQueueChangedListener(
    listener: (event: QueueChangedEvent) => void,
  ): Promise<(() => void) | null> {
    return this.addListener('queueChanged', listener);
  }

  async addRemoteCommandListener(
    listener: (event: RemoteCommandReceivedEvent) => void,
  ): Promise<(() => void) | null> {
    return this.addListener('remoteCommandReceived', listener);
  }

  async addCarConnectionChangedListener(
    listener: (event: CarConnectionChangedEvent) => void,
  ): Promise<(() => void) | null> {
    return this.addListener('carConnectionChanged', listener);
  }

  async addLimitReachedListener(
    listener: (event: LimitReachedEvent) => void,
  ): Promise<(() => void) | null> {
    return this.addListener('limitReached', listener);
  }

  private async addListener<T>(
    eventName:
      | 'playbackStateChanged'
      | 'queueChanged'
      | 'remoteCommandReceived'
      | 'carConnectionChanged'
      | 'limitReached',
    listener: (event: T) => void,
  ): Promise<(() => void) | null> {
    if (!this.isSupported()) {
      return null;
    }

    try {
      let handle: PluginListenerHandle;
      switch (eventName) {
        case 'playbackStateChanged':
          handle = await NativePlayback.addListener(
            'playbackStateChanged',
            listener as (event: PlaybackStateChangedEvent) => void,
          );
          break;
        case 'queueChanged':
          handle = await NativePlayback.addListener(
            'queueChanged',
            listener as (event: QueueChangedEvent) => void,
          );
          break;
        case 'remoteCommandReceived':
          handle = await NativePlayback.addListener(
            'remoteCommandReceived',
            listener as (event: RemoteCommandReceivedEvent) => void,
          );
          break;
        case 'carConnectionChanged':
          handle = await NativePlayback.addListener(
            'carConnectionChanged',
            listener as (event: CarConnectionChangedEvent) => void,
          );
          break;
        case 'limitReached':
          handle = await NativePlayback.addListener(
            'limitReached',
            listener as (event: LimitReachedEvent) => void,
          );
          break;
      }

      return () => {
        void this.removeHandle(handle);
      };
    } catch (error) {
      this.warn(`addListener:${eventName}`, error);
      return null;
    }
  }

  private async removeHandle(handle?: PluginListenerHandle): Promise<void> {
    try {
      await handle?.remove();
    } catch (error) {
      this.warn('removeListener', error);
    }
  }

  private async safeCall<T>(
    action: () => Promise<T>,
    fallback?: T,
  ): Promise<T> {
    if (!this.isSupported()) {
      return fallback as T;
    }

    try {
      return await action();
    } catch (error) {
      this.warn('call', error);
      return fallback as T;
    }
  }

  private warn(scope: string, error: unknown) {
    console.warn(`[${PLUGIN_NAME}] ${scope} failed`, error);
  }
}

export const nativePlaybackBridge = new NativePlaybackBridge();
